import { CircularProgress, Dialog, IconButton, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  PhoneDisabled as PhoneDisabledIcon,
  RecordVoiceOver as GreetingIcon,
  StopCircle as StopIcon,
  Voicemail as VoicemailIcon,
} from "@mui/icons-material";
import { startHumanAnswerDetector } from "../../utils/humanAnswerDetector";
import { VERDICT } from "../../utils/answerClassifier";
import { captureAnalyticsEvent } from "../../utils/posthogAnalytics";
import { brand } from "../../theme/brandTokens";

// Wall-clock cap in case the audio thread never delivers frames (e.g. suspended context).
const DETECTION_SAFETY_TIMEOUT_MS = 10000;
const AUTO_STOP_MISFIRE_WINDOW_MS = 5000;
const DETECTION_OUTCOME_MS = 6000;
const END_NOTICE_MS = 2000;
// Upper bound on waiting for the Dialer's dial-result lookup after an unanswered hangup.
const OUTCOME_WAIT_MS = 5000;
// getRemoteStream() is often null on the accept event; wait briefly for media.
const STREAM_READY_TIMEOUT_MS = 3000;
const STREAM_READY_POLL_MS = 100;

const OngoingCallModal = ({
  open,
  setOpen,
  call_data,
  device,
  connection,
  onUserHangup,
  onVoicemailDropChange,
  endNotice,
  greetingMixerRef,
  greetingRecording,
  vmDropRecording,
  autoGreeting,
  activeCallInfo,
}) => {
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [callConnected, setCallConnected] = useState(false);
  const [callEndedByRecipient, setCallEndedByRecipient] = useState(false);
  const [endedUnanswered, setEndedUnanswered] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [speakerEnabled, setSpeakerEnabled] = useState(true); // placeholder (if controlling output is supported)
  const [greetingState, setGreetingState] = useState("idle"); // idle | listening | loading | playing
  const [vmDropState, setVmDropState] = useState("idle"); // idle | dropping | dropped
  const [audioError, setAudioError] = useState(null);
  const [detectionOutcome, setDetectionOutcome] = useState(null);
  const callConnectedRef = useRef(false);
  const outcomeTimerRef = useRef(null);
  const vmDropStateRef = useRef("idle");
  const detectorRef = useRef(null);
  const detectionTimerRef = useRef(null);
  const streamWaitRef = useRef(null);
  const pendingDecisionRef = useRef(null);
  const autoPlayRef = useRef(null);
  const playbackIdRef = useRef(0);
  const closeTimerRef = useRef(null);

  const scheduleClose = (ms) => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setCallConnected(false);
      setOpen(false);
    }, ms);
  };

  const latest = useRef({});
  latest.current = { greetingRecording, vmDropRecording, autoGreeting, activeCallInfo };

  const setVmDrop = (state) => {
    vmDropStateRef.current = state;
    setVmDropState(state);
  };

  const logDecision = (decision, extra = {}) => {
    captureAnalyticsEvent("greeting_auto_decision", {
      verdict: decision.verdict,
      reason: decision.reason,
      first_utterance_ms: decision.first_utterance_ms ?? null,
      silence_after_ms: decision.silence_after_ms ?? null,
      elapsed_ms: decision.elapsed_ms ?? null,
      call_id: latest.current.activeCallInfo?.callId ?? null,
      played: false,
      rep_stopped_within_5s: false,
      ...extra,
    });
  };

  const clearDetectionOutcome = () => {
    clearTimeout(outcomeTimerRef.current);
    setDetectionOutcome(null);
  };

  const showDetectionOutcome = ({ verdict, reason }) => {
    const notPlayed = "Greeting not played.";
    const message =
      verdict === VERDICT.HUMAN
        ? "Person detected. Playing your greeting."
        : verdict === VERDICT.MACHINE
        ? latest.current.vmDropRecording
          ? `Automated service detected. ${notPlayed} Use the voicemail button to leave a message.`
          : `Automated service detected. ${notPlayed}`
        : verdict === VERDICT.REP_SPOKE
        ? `Rep speech detected. ${notPlayed}`
        : reason === "no_speech"
        ? `No response detected. ${notPlayed}`
        : `Unable to identify who answered. ${notPlayed}`;
    clearTimeout(outcomeTimerRef.current);
    setDetectionOutcome(message);
    outcomeTimerRef.current = setTimeout(() => setDetectionOutcome(null), DETECTION_OUTCOME_MS);
  };

  const cancelStreamWait = () => {
    const wait = streamWaitRef.current;
    if (!wait) return;
    wait.cancelled = true;
    wait.cleanup?.();
    streamWaitRef.current = null;
    wait.resolve?.({ cancelled: true });
  };

  const stopDetection = () => {
    cancelStreamWait();
    detectorRef.current?.stop();
    detectorRef.current = null;
    clearTimeout(detectionTimerRef.current);
    detectionTimerRef.current = null;
  };

  const cancelDetection = (reason, verdict = "rep_override") => {
    const waiting = !!streamWaitRef.current;
    const detecting = !!detectorRef.current;
    cancelStreamWait();
    if (!detecting) {
      if (waiting) {
        console.info("[Greeting] detection wait cancelled:", reason);
      }
      return;
    }
    stopDetection();
    logDecision({ verdict, reason });
  };

  /**
   * Twilio often fires `accept` before getRemoteStream() is set. Poll + listen
   * for volume until the mixer is active and the remote stream exists.
   */
  const waitForDetectionInputs = (mixer) =>
    new Promise((resolve) => {
      const startedAt = Date.now();
      const state = { cancelled: false, cleanup: null, resolve: null, done: false };
      streamWaitRef.current = state;

      const finish = (result) => {
        if (state.done) return;
        state.done = true;
        state.cleanup?.();
        if (streamWaitRef.current === state) streamWaitRef.current = null;
        resolve(result);
      };
      state.resolve = finish;

      const snapshot = () => {
        const active = !!mixer?.isCallActive?.();
        const remoteStream = connection?.getRemoteStream?.() || null;
        const ctx = mixer?.getContext?.() || null;
        return {
          mixer_active: active,
          has_remote_stream: !!remoteStream,
          has_context: !!ctx,
          remote_tracks: remoteStream?.getAudioTracks?.()?.length ?? 0,
          context_state: ctx?.state ?? null,
          remoteStream,
          ctx,
        };
      };

      const check = (trigger) => {
        if (state.cancelled) {
          finish({ cancelled: true });
          return true;
        }
        const snap = snapshot();
        const elapsed_ms = Date.now() - startedAt;
        console.info("[Greeting] detection inputs check", {
          trigger,
          elapsed_ms,
          mixer_active: snap.mixer_active,
          has_remote_stream: snap.has_remote_stream,
          has_context: snap.has_context,
          remote_tracks: snap.remote_tracks,
          context_state: snap.context_state,
        });
        if (snap.mixer_active && snap.remoteStream && snap.ctx) {
          finish({
            remoteStream: snap.remoteStream,
            ctx: snap.ctx,
            waited_ms: elapsed_ms,
          });
          return true;
        }
        if (elapsed_ms >= STREAM_READY_TIMEOUT_MS) {
          const error = !snap.mixer_active
            ? "mixer_inactive"
            : !snap.remoteStream
            ? "no_remote_stream"
            : "no_audio_context";
          finish({ error, waited_ms: elapsed_ms });
          return true;
        }
        return false;
      };

      if (check("immediate")) return;

      const onVolume = () => check("volume");
      connection?.on?.("volume", onVolume);
      const interval = setInterval(() => check("poll"), STREAM_READY_POLL_MS);
      state.cleanup = () => {
        clearInterval(interval);
        connection?.off?.("volume", onVolume);
      };
    });

  const playGreeting = async (trigger) => {
    const mixer = greetingMixerRef?.current;
    const recording = latest.current.greetingRecording;
    if (!mixer || !recording) return;
    const playbackId = ++playbackIdRef.current;
    setAudioError(null);
    setGreetingState("loading");
    const startedAt = Date.now();
    if (trigger === "auto") {
      autoPlayRef.current = { startedAt, repStopped: false };
    }
    try {
      const playback = mixer.play(recording.url);
      if (playbackId === playbackIdRef.current) setGreetingState("playing");
      captureAnalyticsEvent("greeting_played", {
        trigger,
        call_id: latest.current.activeCallInfo?.callId ?? null,
      });
      await playback;
    } catch (error) {
      console.error("OngoingCallModal: greeting playback failed", error);
      if (playbackId === playbackIdRef.current) setAudioError("Couldn’t play the greeting.");
    } finally {
      if (playbackId === playbackIdRef.current) setGreetingState("idle");
      if (trigger === "auto" && pendingDecisionRef.current) {
        const auto = autoPlayRef.current;
        logDecision(pendingDecisionRef.current, {
          played: true,
          rep_stopped_within_5s:
            !!auto?.repStopped && Date.now() - startedAt <= AUTO_STOP_MISFIRE_WINDOW_MS,
        });
        pendingDecisionRef.current = null;
        autoPlayRef.current = null;
      }
    }
  };

  const startAutoDetection = async () => {
    const { greetingRecording: recording, autoGreeting: enabled } = latest.current;
    const mixer = greetingMixerRef?.current;

    if (!recording) {
      console.info("[Greeting] auto-play skipped: no_greeting");
      return;
    }
    if (!enabled) {
      console.info("[Greeting] auto-play skipped: auto_off");
      return;
    }
    if (!mixer) {
      console.warn("[Greeting] auto-play skipped: no_mixer");
      const decision = { verdict: VERDICT.UNKNOWN, reason: "no_mixer" };
      showDetectionOutcome(decision);
      logDecision(decision);
      return;
    }
    if (detectorRef.current || streamWaitRef.current) {
      console.info("[Greeting] auto-play skipped: already_running");
      return;
    }

    clearDetectionOutcome();
    setGreetingState("listening");
    console.info("[Greeting] waiting for remote stream / mixer after accept");
    mixer.preload(recording.url);

    const ready = await waitForDetectionInputs(mixer);
    if (ready?.cancelled) {
      console.info("[Greeting] detection wait cancelled before start");
      return;
    }
    if (ready?.error) {
      console.warn("[Greeting] auto-play skipped:", ready.error, {
        waited_ms: ready.waited_ms,
      });
      setGreetingState("idle");
      const decision = {
        verdict: VERDICT.UNKNOWN,
        reason: ready.error,
        elapsed_ms: ready.waited_ms,
      };
      showDetectionOutcome(decision);
      logDecision(decision);
      return;
    }

    console.info("[Greeting] starting detector", {
      waited_ms: ready.waited_ms,
      context_state: ready.ctx?.state,
      remote_tracks: ready.remoteStream?.getAudioTracks?.()?.length ?? 0,
    });

    detectorRef.current = startHumanAnswerDetector({
      ctx: ready.ctx,
      remoteStream: ready.remoteStream,
      micStream: mixer.getInputStream(),
      onDecision: (decision) => {
        if (!detectorRef.current) return;
        console.info("[Greeting] auto-play decision:", decision);
        stopDetection();
        if (vmDropStateRef.current !== "idle") {
          setGreetingState("idle");
          logDecision(decision);
          return;
        }
        showDetectionOutcome(decision);
        if (decision.verdict === VERDICT.HUMAN) {
          pendingDecisionRef.current = decision;
          playGreeting("auto");
        } else {
          setGreetingState("idle");
          logDecision(decision);
        }
      },
    });
    detectionTimerRef.current = setTimeout(() => {
      if (!detectorRef.current) return;
      const decision = { verdict: VERDICT.UNKNOWN, reason: "safety_timeout" };
      console.info("[Greeting] auto-play decision:", decision);
      stopDetection();
      setGreetingState("idle");
      showDetectionOutcome(decision);
      logDecision(decision);
    }, DETECTION_SAFETY_TIMEOUT_MS);
  };

  const resetCallAudio = () => {
    cancelDetection("call_ended", "call_ended");
    greetingMixerRef?.current?.stop();
    setGreetingState("idle");
    clearDetectionOutcome();
  };

  useEffect(() => {
    let timer;

    if (open && device) {
      setSecondsElapsed(0);
      setCallConnected(false);
      callConnectedRef.current = false;
      setCallEndedByRecipient(false);
      setEndedUnanswered(false);
      setVmDrop("idle");
      setAudioError(null);
      setGreetingState("idle");

      const handleConnect = () => {
        callConnectedRef.current = true;
        setCallConnected(true);
        timer = setInterval(() => {
          setSecondsElapsed((prev) => prev + 1);
        }, 1000);
        startAutoDetection();
      };

      const handleDisconnect = () => {
        clearInterval(timer);
        resetCallAudio();
        if (vmDropStateRef.current !== "idle") {
          setVmDrop("dropped");
          scheduleClose(END_NOTICE_MS);
        } else if (callConnectedRef.current) {
          setCallEndedByRecipient(true);
          scheduleClose(END_NOTICE_MS);
        } else {
          // The Dialer looks up why the call ended and either passes endNotice
          // (busy / no answer) or closes this modal and shows an error.
          setEndedUnanswered(true);
          scheduleClose(OUTCOME_WAIT_MS);
        }
      };

      connection?.on("accept", handleConnect);
      connection?.on("disconnect", handleDisconnect);
      connection?.on("cancel", handleDisconnect);
      connection?.on("reject", handleDisconnect);

      return () => {
        clearInterval(timer);
        clearTimeout(closeTimerRef.current);
        resetCallAudio();
        connection?.off("accept", handleConnect);
        connection?.off("disconnect", handleDisconnect);
        connection?.off("cancel", handleDisconnect);
        connection?.off("reject", handleDisconnect);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device, connection, setOpen]);

  useEffect(() => {
    if (open && endNotice) scheduleClose(END_NOTICE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, endNotice]);

  // DTMF (touch-tone) support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (connection && e.key.match(/^[0-9*#]$/)) {
        connection.sendDigits(e.key);
      }
    };
    if (open) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, connection]);

  const toggleMute = () => {
    if (connection) {
      connection.mute(!isMuted);
      setIsMuted((prev) => !prev);
    }
  };

  const endCall = () => {
    onUserHangup?.();
    if (device) device.disconnectAll();
    setOpen(false);
  };

  const handleGreetingClick = () => {
    clearDetectionOutcome();
    if (greetingState === "playing" || greetingState === "loading") {
      if (autoPlayRef.current) autoPlayRef.current.repStopped = true;
      greetingMixerRef?.current?.stop();
      setGreetingState("idle");
      return;
    }
    if (greetingState === "listening") {
      cancelDetection("manual_play");
    }
    playGreeting("manual");
  };

  const handleVoicemailDrop = async () => {
    const info = latest.current.activeCallInfo;
    const recording = latest.current.vmDropRecording;
    const callSid = connection?.parameters?.CallSid;
    if (!recording || !info?.orgId || !callSid) return;

    cancelDetection("voicemail_drop");
    clearDetectionOutcome();
    greetingMixerRef?.current?.stop();
    setGreetingState("idle");
    setAudioError(null);
    setVmDrop("dropping");
    // The rep's leg can disconnect before this request returns.
    onUserHangup?.(true);
    onVoicemailDropChange?.(true);
    try {
      await axios.post(process.env.REACT_APP_BACKEND_URL + "twilio/drop-voicemail", {
        organization_id: info.orgId,
        call_sid: callSid,
        call_id: info.callId || undefined,
        recording_id: recording.recId,
      });
      captureAnalyticsEvent("voicemail_dropped", { call_id: info.callId ?? null });
    } catch (error) {
      console.error(
        "OngoingCallModal: voicemail drop failed",
        error.response?.data || error.message || error
      );
      onUserHangup?.(false);
      onVoicemailDropChange?.(false);
      setVmDrop("idle");
      setAudioError(error.response?.data?.error || "Couldn’t drop the voicemail. You’re still on the call.");
    }
  };

  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60).toString().padStart(2, "0");
    const secsRemaining = (secs % 60).toString().padStart(2, "0");
    return `${mins}:${secsRemaining}`;
  };

  const callLive = callConnected && !callEndedByRecipient && vmDropState === "idle";

  const greetingTooltip = !greetingRecording
    ? "No greeting selected. Choose one in the Dialer’s Advanced Settings before your next call."
    : greetingState === "listening"
    ? `Listening for who answered. Click to play “${greetingRecording.title}” now.`
    : greetingState === "playing" || greetingState === "loading"
    ? "Stop greeting"
    : !callConnected
    ? `Play “${greetingRecording.title}” once the call is answered`
    : `Play greeting: “${greetingRecording.title}”`;

  const vmDropTooltip = !vmDropRecording
    ? "No voicemail drop selected. Choose one in the Dialer’s Advanced Settings before your next call."
    : !callConnected
    ? "Available once the call is answered"
    : `Click once you hear voicemail. “${vmDropRecording.title}” plays after the greeting ends, and you’re free right away.`;

  const greetingDisabled = !greetingRecording || !callLive;
  const vmDropDisabled = !vmDropRecording || !callLive;

  const headline =
    vmDropState !== "idle"
      ? "Leaving Voicemail"
      : endNotice && endedUnanswered
      ? endNotice
      : callEndedByRecipient
      ? "Call Ended By Recipient"
      : endedUnanswered
      ? "Call Ended"
      : callConnected
      ? formatTime(secondsElapsed)
      : "Ringing...";

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={"xs"}
      BackdropProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.2)" } }}
      PaperProps={{ className: "rounded-xl overflow-hidden" }}
    >
      <div className="bg-white flex flex-col items-center text-center py-6 px-4 space-y-4">
        <div className="text-[24px] font-semibold text-gray-800">{headline}</div>

        <div className="text-[16px] text-gray-600 flex flex-col items-center">
          {vmDropState !== "idle" ? (
            <span>The message will play after the voicemail greeting ends. You’re free to make your next call.</span>
          ) : (
            <>
              Calling:{" "}
              <span className="font-medium">
                {call_data?.recipient || "Unknown"}
              </span>
            </>
          )}
        </div>

        <div className="flex items-center gap-4 mt-2">
          <IconButton onClick={toggleMute}>
            {isMuted ? <MicOffIcon /> : <MicIcon />}
          </IconButton>

          <Tooltip title={greetingTooltip} arrow>
            <span>
              <IconButton onClick={handleGreetingClick} disabled={greetingDisabled}>
                {greetingState === "loading" ? (
                  <CircularProgress size={22} />
                ) : greetingState === "playing" ? (
                  <StopIcon style={{ color: brand.primary }} />
                ) : (
                  <GreetingIcon
                    className={greetingState === "listening" ? "animate-pulse" : ""}
                    style={greetingDisabled ? undefined : { color: brand.primary }}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={vmDropTooltip} arrow>
            <span>
              <IconButton onClick={handleVoicemailDrop} disabled={vmDropDisabled}>
                {vmDropState === "dropping" ? (
                  <CircularProgress size={22} />
                ) : (
                  <VoicemailIcon style={vmDropDisabled ? undefined : { color: brand.primary }} />
                )}
              </IconButton>
            </span>
          </Tooltip>

          {/* Placeholder speaker control — typically handled by the system, or via custom AudioContext */}
          {/* <IconButton
            onClick={() => setSpeakerEnabled((prev) => !prev)}
            disabled
          >
            {speakerEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
          </IconButton> */}

          <IconButton onClick={endCall}>
            <PhoneDisabledIcon style={{ color: "red" }} />
          </IconButton>
        </div>

        {greetingState === "listening" ? (
          <p className="text-xs text-gray-500">Listening for who answered…</p>
        ) : (
          detectionOutcome && <p className="text-xs text-gray-500">{detectionOutcome}</p>
        )}
        {audioError && <p className="text-xs text-red-600">{audioError}</p>}

        <p className="text-xs text-gray-400">
          Press keypad (0-9, *, #) to send DTMF tones
        </p>
      </div>
    </Dialog>
  );
};

export default OngoingCallModal;
